import { ModalBody, ModalContent, ModalFooter, ModalHeader, ModalWrapper } from "@src/components/Modal/Modal.styled";
import { CloseIcon } from "@src/assets/icons/CloseIcon";
import React, { forwardRef, ReactNode, useImperativeHandle, useState } from "react";
import { Button } from "@src/components/Button/Button";
import { ButtonVariant } from "@src/components/Button/Button.constants";
import { RefreshIcon } from "@src/assets/icons/RefreshIcon";
import { createPortal } from "react-dom";
import { Tooltip } from "react-tooltip";

interface ModalProps {
	onSave?: () => void;
	onClose?: () => void;
	children: ReactNode;
	title?: string;
	onRefresherClick?: () => void;
	maxWidth?: string;
	cancelButtonLabel?: string;
	okButtonLabel?: string;
	customButton?: React.ReactNode;
	hasFooter?: boolean;
	isLoading?: boolean;
	onSecondary?: () => void;
	secondaryButtonLabel?: string;
	onTertiary?: () => void;
	tertiaryButtonLabel?: string;
	isTertiaryDisabled?: boolean;
	okButtonTooltipId?: string;
	okButtonTooltipContent?: string;
}

export interface ModalHandle {
	openModal: () => void;
	closeModal: () => void;
}

const ModalFR: React.ForwardRefRenderFunction<ModalHandle, ModalProps> = (
	{
		onSave,
		children,
		title,
		onRefresherClick,
		maxWidth,
		cancelButtonLabel = "Cancel",
		okButtonLabel = "Save",
		customButton,
		hasFooter = true,
		isLoading,
		onClose,
		onSecondary,
		secondaryButtonLabel = "Secondary",
		onTertiary,
		tertiaryButtonLabel = "Tertiary",
		isTertiaryDisabled = false,
		okButtonTooltipId,
		okButtonTooltipContent,
	}: ModalProps,
	ref,
) => {
	const [isOpen, setIsOpen] = useState(false);

	const openModal = () => setIsOpen(true);
	const closeModal = () => {
		setIsOpen(false);
		if (onClose) onClose();
	};

	useImperativeHandle(ref, () => ({
		openModal,
		closeModal,
	}));

	return (
		<>
			{customButton}
			{isOpen &&
				createPortal(
					<ModalWrapper>
						<ModalContent $maxWidth={maxWidth}>
							<ModalHeader>
								{title ?? <p></p>}
								<CloseIcon onClick={closeModal} />
							</ModalHeader>
							<ModalBody>{children}</ModalBody>
							{hasFooter && (
								<ModalFooter>
									{onRefresherClick && (
										<Button onClick={onRefresherClick} variant={ButtonVariant.Tertiary}>
											<RefreshIcon />
										</Button>
									)}
									<Button onClick={closeModal} variant={ButtonVariant.Tertiary} style={{ padding: "6px 16px" }}>
										{cancelButtonLabel}
									</Button>
									<div style={{
										display: "flex",
										gap: "8px"
									}}>
										{onTertiary && (
											<Button
												onClick={onTertiary}
												variant={ButtonVariant.Tertiary}
												style={{ padding: "6px 16px" }}
												disabled={isTertiaryDisabled}
											>
												{tertiaryButtonLabel}
											</Button>
										)}
										{onSecondary && (
											<Button
												onClick={onSecondary}
												variant={ButtonVariant.Primary}
												style={{ padding: "6px 16px" }}
											>
												{secondaryButtonLabel}
											</Button>
										)}
										<Button
											type="submit"
											onClick={onSave}
											variant={ButtonVariant.Secondary}
											style={{ padding: "6px 16px" }}
											data-tooltip-id={okButtonTooltipContent ? okButtonTooltipId : undefined}
											data-tooltip-content={okButtonTooltipContent}
											isLoading={isLoading}>
											{okButtonLabel}
										</Button>
										{okButtonTooltipId && okButtonTooltipContent && (
											<Tooltip
												id={okButtonTooltipId}
												place="top"
												style={{ zIndex: 9999, fontSize: "1.2rem", lineHeight: 1.35 }}
											/>
										)}
									</div>
								</ModalFooter>
							)}
						</ModalContent>
					</ModalWrapper>,
					document.querySelector("#modal") as HTMLElement,
				)}
		</>
	);
};

export const Modal = forwardRef(ModalFR);
